import React, { useState, useEffect } from 'react';
import { providersApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { ProviderDto, TestConnectionResultDto } from '@shared/contracts';
import { Cpu, Key, CheckCircle, XCircle, Zap, Play, Plus, Trash2, Sparkles, Star } from 'lucide-react';

interface PresetEngine {
  slug: string;
  name: string;
  badgeColor: string;
  iconColor: string;
  defaultModel: string;
  suggestedModels: string[];
}

const PRESET_ENGINES: PresetEngine[] = [
  {
    slug: 'litellm',
    name: 'Sensifai Fast Engine',
    badgeColor: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    iconColor: 'text-emerald-600',
    defaultModel: 'sensifai-fast',
    suggestedModels: ['sensifai-fast', 'sensifai-reasoning', 'sensifai-vision'],
  },
  {
    slug: 'litellm',
    name: 'Sensifai Reasoning Engine',
    badgeColor: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    iconColor: 'text-indigo-600',
    defaultModel: 'sensifai-reasoning',
    suggestedModels: ['sensifai-reasoning', 'sensifai-fast', 'sensifai-vision'],
  },
  {
    slug: 'litellm',
    name: 'Sensifai Vision Engine',
    badgeColor: 'bg-sky-50 text-sky-700 border-sky-200',
    iconColor: 'text-sky-600',
    defaultModel: 'sensifai-vision',
    suggestedModels: ['sensifai-vision', 'sensifai-fast', 'sensifai-reasoning'],
  },
];

export const AiProvidersPage: React.FC = () => {
  const { user, isSuperAdmin, selectActiveModel } = useAuth();
  const [providers, setProviders] = useState<ProviderDto[]>([]);
  const [, setLoading] = useState(true);
  
  // Modals & Active state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<ProviderDto | null>(null);
  const [deletingProvider, setDeletingProvider] = useState<ProviderDto | null>(null);
  
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<TestConnectionResultDto | null>(null);

  // Form State
  const [nameInput, setNameInput] = useState('');
  const [slugInput, setSlugInput] = useState('litellm');
  const [modelInput, setModelInput] = useState('');
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [baseUrlInput, setBaseUrlInput] = useState('');
  const [priorityInput, setPriorityInput] = useState(1);
  const [isActiveInput, setIsActiveInput] = useState(true);
  const [isDefaultInput, setIsDefaultInput] = useState(false);
  
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const fetchProviders = async () => {
    try {
      if (isSuperAdmin) {
        const data = await providersApi.list();
        setProviders(data || []);
      } else {
        const data = await providersApi.listActive();
        setProviders(data || []);
      }
    } catch (e) {
      console.error('Failed to fetch AI providers', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, [isSuperAdmin]);

  const handleOpenAddModal = () => {
    const defaultEngine = PRESET_ENGINES[0];
    setSlugInput(defaultEngine.slug);
    setNameInput(`${defaultEngine.name} (${defaultEngine.defaultModel})`);
    setModelInput(defaultEngine.defaultModel);
    setApiKeyInput('');
    setBaseUrlInput('');
    setPriorityInput(providers.length + 1);
    setIsActiveInput(true);
    setIsDefaultInput(providers.length === 0);
    setIsAddModalOpen(true);
  };

  const handleSelectEngine = (engineSlug: string) => {
    setSlugInput(engineSlug);
    const engine = PRESET_ENGINES.find((e) => e.slug === engineSlug) || PRESET_ENGINES[0];
    setModelInput(engine.defaultModel);
    setNameInput(`${engine.name} (${engine.defaultModel})`);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      const newProv = await providersApi.create({
        name: nameInput,
        slug: slugInput,
        defaultModel: modelInput,
        apiKey: apiKeyInput,
        baseUrl: baseUrlInput,
        priority: priorityInput,
        isActive: isActiveInput,
        isDefault: isDefaultInput,
      });
      setMessage(`Successfully added model: ${newProv.name}`);
      setIsAddModalOpen(false);
      fetchProviders();
    } catch (e) {
      const err = e as Error;
      setMessage('Error adding AI Model: ' + (err.message || 'Create failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (p: ProviderDto) => {
    setEditingProvider(p);
    setNameInput(p.name);
    setSlugInput(p.slug || 'openai');
    setModelInput(p.defaultModel || '');
    setApiKeyInput('');
    setBaseUrlInput(p.baseUrl || '');
    setPriorityInput(p.priority || 1);
    setIsActiveInput(p.isActive ?? true);
    setIsDefaultInput(p.isDefault ?? false);
  };

  const handleSaveUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProvider) return;
    setSaving(true);
    setMessage('');
    try {
      await providersApi.update(editingProvider.id, {
        name: nameInput,
        slug: slugInput,
        defaultModel: modelInput,
        apiKey: apiKeyInput ? apiKeyInput : 'KEEP_UNCHANGED',
        baseUrl: baseUrlInput,
        priority: priorityInput,
        isActive: isActiveInput,
        isDefault: isDefaultInput,
      });
      setMessage(`Successfully updated ${nameInput}`);
      setEditingProvider(null);
      fetchProviders();
    } catch (e) {
      const err = e as Error;
      setMessage('Error updating AI Provider: ' + (err.message || 'Update failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!deletingProvider) return;
    setSaving(true);
    try {
      await providersApi.delete(deletingProvider.id);
      setMessage(`Deleted AI Model: ${deletingProvider.name}`);
      setDeletingProvider(null);
      fetchProviders();
    } catch (e) {
      const err = e as Error;
      setMessage('Error deleting AI Provider: ' + (err.message || 'Delete failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async (id: string) => {
    setTestingId(id);
    setTestResult(null);
    try {
      const res = await providersApi.testConnection(id);
      setTestResult(res);
    } catch (err) {
      const error = err as Error;
      setTestResult({ id, status: 'FAILED', message: error.message, latencyMs: 0 });
    } finally {
      setTestingId(null);
    }
  };

  const getEngineBadge = (slug: string) => {
    const s = (slug || '').toLowerCase();
    if (s.includes('litellm') || s.includes('sensifai')) {
      return { label: 'Sensifai Gateway', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    }
    return { label: 'Sensifai Gateway', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
  };

  const currentEngineObj = PRESET_ENGINES.find((e) => e.slug === slugInput) || PRESET_ENGINES[0];

  if (!isSuperAdmin) {
    const currentActiveId = user?.activeModelId || (providers.length === 1 ? providers[0]?.id : undefined);

    return (
      <div className="space-y-6 max-w-5xl mx-auto font-sans">
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Cpu className="w-5 h-5 text-sky-600" />
              <span>Active AI Models</span>
            </h1>
            <p className="text-xs text-slate-500 mt-1">
              Select an active AI model configured by Super Admin to process your document jobs.
            </p>
          </div>
        </div>

        {providers.length === 1 && (
          <div className="p-4 rounded-xl bg-sky-50 border border-sky-200 text-sky-800 text-xs flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-sky-600 shrink-0" />
            <div>
              <span className="font-bold">Single Active Model Enabled:</span> The model{' '}
              <span className="font-semibold text-sky-900">{providers[0].name}</span> is automatically activated for your workspace.
            </div>
          </div>
        )}

        {providers.length === 0 ? (
          <div className="bg-white p-12 rounded-2xl border border-slate-200 text-center space-y-3">
            <Cpu className="w-10 h-10 text-slate-300 mx-auto" />
            <h3 className="text-base font-bold text-slate-700">No Active AI Models Available</h3>
            <p className="text-xs text-slate-500 max-w-md mx-auto">
              Super Admin has not activated any AI models yet. Please contact your Super Admin to enable AI engines.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {providers.map((p) => {
              const isSelected = currentActiveId === p.id || (providers.length === 1 && p.isActive);
              return (
                <div
                  key={p.id}
                  className={`bg-white p-5 rounded-2xl border transition-all ${
                    isSelected
                      ? 'border-sky-500 ring-2 ring-sky-500/20 shadow-md'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>

                      <h3 className="text-base font-bold text-slate-900 mt-2">{p.name}</h3>
                      <p className="text-xs text-slate-500 font-mono mt-0.5">Default Model: {p.defaultModel}</p>
                    </div>

                    {isSelected ? (
                      <span className="flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
                        <CheckCircle className="w-3.5 h-3.5" />
                        <span>Selected & Active</span>
                      </span>
                    ) : (
                      <button
                        onClick={() => selectActiveModel(p.id)}
                        className="px-3 py-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold shadow-sm transition-all"
                      >
                        Select Model
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Cpu className="w-5 h-5 text-sky-600" />
            <span>AI Engines</span>
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Configure Sensifai LiteLLM AI Gateway models, Virtual Keys (sk-...), and failover priority.
          </p>
        </div>

        <button
          onClick={handleOpenAddModal}
          className="px-4 py-2 rounded-xl btn-primary text-xs font-semibold flex items-center gap-1.5 shadow-sm"
        >
          <Plus className="w-4 h-4" />
          <span>Add AI Model</span>
        </button>
      </div>

      {message && (
        <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-medium flex items-center justify-between">
          <span>{message}</span>
          <button onClick={() => setMessage('')} className="text-slate-400 hover:text-slate-700 text-xs">
            ✕
          </button>
        </div>
      )}

      {/* Provider Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {providers.map((p) => {
          const engineInfo = getEngineBadge(p.slug);
          return (
            <div key={p.id} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4 flex flex-col justify-between relative">
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5">
                    <Zap className="w-5 h-5 text-sky-600 shrink-0" />
                    <div>
                      <h3 className="text-base font-bold text-slate-900 leading-snug">{p.name}</h3>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-semibold uppercase border ${engineInfo.color}`}>
                          {engineInfo.label}
                        </span>
                        {p.isDefault && (
                          <span className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-1">
                            <Star className="w-2.5 h-2.5 fill-amber-500 text-amber-500" />
                            Default
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <span
                    className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase border shrink-0 ${
                      p.isActive
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-slate-100 text-slate-500 border-slate-200'
                    }`}
                  >
                    {p.isActive ? 'Active' : 'Disabled'}
                  </span>
                </div>

                <div className="space-y-1.5 text-xs font-mono text-slate-700 bg-slate-50 p-3 rounded-xl border border-slate-200">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">Model:</span>
                    <span className="font-semibold text-slate-900 truncate max-w-[170px]" title={p.defaultModel}>
                      {p.defaultModel}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">Virtual Key:</span>
                    <span className={p.hasApiKey ? 'text-emerald-700 font-semibold' : 'text-amber-600'}>
                      {p.apiKeyMasked}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">Priority:</span>
                    <span className="text-slate-800 font-semibold">#{p.priority}</span>
                  </div>
                </div>

                {testResult && testResult.id === p.id && (
                  <div
                    className={`p-2.5 rounded-xl border text-xs font-mono flex items-center gap-2 ${
                      testResult.status === 'SUCCESS'
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                        : 'bg-red-50 border-red-200 text-red-700'
                    }`}
                  >
                    {testResult.status === 'SUCCESS' ? <CheckCircle className="w-4 h-4 shrink-0 text-emerald-600" /> : <XCircle className="w-4 h-4 shrink-0 text-red-600" />}
                    <div className="truncate">
                      <span>{testResult.message}</span>
                      <span className="block text-[10px] text-slate-500">{testResult.latencyMs}ms latency</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
                <button
                  onClick={() => handleTestConnection(p.id)}
                  disabled={testingId === p.id}
                  className="flex-1 px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700 text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer"
                >
                  <Play className="w-3.5 h-3.5" />
                  <span>{testingId === p.id ? 'Testing...' : 'Test'}</span>
                </button>

                <button
                  onClick={() => handleEdit(p)}
                  className="px-3 py-1.5 rounded-xl bg-sky-50 hover:bg-sky-100 border border-sky-200 text-sky-700 text-xs font-semibold transition-all cursor-pointer"
                >
                  Edit
                </button>

                <button
                  onClick={() => setDeletingProvider(p)}
                  title="Delete AI Model"
                  className="p-1.5 rounded-xl bg-red-50 hover:bg-red-100 border border-red-200 text-red-600 transition-all cursor-pointer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add New AI Provider Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="w-full max-w-xl bg-white p-6 rounded-2xl border border-slate-200 shadow-xl space-y-5 my-8">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Plus className="w-4 h-4 text-sky-600" />
                <span>Add AI Model</span>
              </h2>
              <button onClick={() => setIsAddModalOpen(false)} className="text-slate-400 hover:text-slate-700 text-xs cursor-pointer">
                ✕
              </button>
            </div>

            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                  <Sparkles className="w-3.5 h-3.5 text-sky-600" />
                  <span>Select AI Engine</span>
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {PRESET_ENGINES.map((eng) => {
                    const isSelected = slugInput === eng.slug;
                    return (
                      <button
                        key={eng.slug}
                        type="button"
                        onClick={() => handleSelectEngine(eng.slug)}
                        className={`p-2.5 rounded-xl border text-left flex flex-col justify-between transition-all cursor-pointer ${
                          isSelected
                            ? 'bg-sky-50 border-sky-200 text-slate-900 font-semibold'
                            : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        <span className="text-xs font-semibold">{eng.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">Title</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Sensifai Fast"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700">Model Identifier</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. sensifai-fast, sensifai-reasoning, sensifai-vision"
                  value={modelInput}
                  onChange={(e) => setModelInput(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-800 font-mono focus:outline-none focus:ring-2 focus:ring-sky-500"
                />

                <div className="flex flex-wrap gap-1.5 items-center pt-0.5">
                  <span className="text-[10px] text-slate-400">Presets:</span>
                  {currentEngineObj.suggestedModels.map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => {
                        setModelInput(m);
                        setNameInput(`${currentEngineObj.name} (${m})`);
                      }}
                      className={`px-2 py-0.5 rounded-md text-[10px] font-mono border transition-all cursor-pointer ${
                        modelInput === m
                          ? 'bg-sky-50 text-sky-700 border-sky-200'
                          : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">Virtual Key (sk-...)</label>
                <input
                  type="password"
                  placeholder="sk-..."
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-800 font-mono focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-700">Priority</label>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={priorityInput}
                    onChange={(e) => setPriorityInput(Number(e.target.value))}
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-1.5 text-xs text-slate-800 font-mono"
                  />
                </div>

                <div className="flex items-center gap-2 pt-4">
                  <input
                    type="checkbox"
                    id="addIsActive"
                    checked={isActiveInput}
                    onChange={(e) => setIsActiveInput(e.target.checked)}
                    className="rounded border-slate-300 text-sky-600 focus:ring-sky-500 cursor-pointer"
                  />
                  <label htmlFor="addIsActive" className="text-xs font-semibold text-slate-700 cursor-pointer">
                    Enable
                  </label>
                </div>

                <div className="flex items-center gap-2 pt-4">
                  <input
                    type="checkbox"
                    id="addIsDefault"
                    checked={isDefaultInput}
                    onChange={(e) => setIsDefaultInput(e.target.checked)}
                    className="rounded border-slate-300 text-sky-600 focus:ring-sky-500 cursor-pointer"
                  />
                  <label htmlFor="addIsDefault" className="text-xs font-semibold text-slate-700 cursor-pointer">
                    Default
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-slate-600 hover:text-slate-900 text-xs font-medium cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2 rounded-xl btn-primary text-xs font-semibold shadow-sm cursor-pointer"
                >
                  {saving ? 'Adding...' : 'Add AI Model'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Provider Modal */}
      {editingProvider && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="w-full max-w-lg bg-white p-6 rounded-2xl border border-slate-200 shadow-xl space-y-5 my-8">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Key className="w-4 h-4 text-sky-600" />
                <span>Configure {editingProvider.name}</span>
              </h2>
              <button onClick={() => setEditingProvider(null)} className="text-slate-400 hover:text-slate-700 text-xs cursor-pointer">
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveUpdate} className="space-y-3.5">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">Configuration Name</label>
                <input
                  type="text"
                  required
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">Engine Slug</label>
                <select
                  value={slugInput}
                  onChange={(e) => setSlugInput(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-500 font-mono"
                >
                  <option value="litellm">Sensifai  Gateway</option>
                  <option value="custom">Custom Alias (custom)</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">Model Alias</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. sensifai-fast, sensifai-reasoning, sensifai-vision"
                  value={modelInput}
                  onChange={(e) => setModelInput(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-800 font-mono focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">Virtual Key (sk-...)</label>
                <input
                  type="password"
                  placeholder="Enter Virtual Key (leave empty to keep unchanged)"
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-800 font-mono focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-700">Priority</label>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={priorityInput}
                    onChange={(e) => setPriorityInput(Number(e.target.value))}
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-1.5 text-xs text-slate-800 font-mono"
                  />
                </div>

                <div className="flex items-center gap-2 pt-2 sm:pt-4">
                  <input
                    type="checkbox"
                    id="editIsActive"
                    checked={isActiveInput}
                    onChange={(e) => setIsActiveInput(e.target.checked)}
                    className="rounded border-slate-300 text-sky-600 focus:ring-sky-500 cursor-pointer"
                  />
                  <label htmlFor="editIsActive" className="text-xs font-semibold text-slate-700 cursor-pointer">
                    Enabled
                  </label>
                </div>

                <div className="flex items-center gap-2 pt-4">
                  <input
                    type="checkbox"
                    id="editIsDefault"
                    checked={isDefaultInput}
                    onChange={(e) => setIsDefaultInput(e.target.checked)}
                    className="rounded border-slate-300 text-sky-600 focus:ring-sky-500 cursor-pointer"
                  />
                  <label htmlFor="editIsDefault" className="text-xs font-semibold text-slate-700 cursor-pointer">
                    Default
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setEditingProvider(null)}
                  className="px-4 py-2 rounded-xl text-slate-600 hover:text-slate-900 text-xs font-medium cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2 rounded-xl btn-primary text-xs font-semibold shadow-sm cursor-pointer"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingProvider && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="w-full max-w-md bg-white p-6 rounded-2xl border border-slate-200 shadow-xl space-y-4">
            <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Trash2 className="w-4 h-4 text-red-600" />
              <span>Delete Configuration?</span>
            </h3>
            <p className="text-xs text-slate-600">
              Are you sure you want to remove <strong className="text-slate-900">{deletingProvider.name}</strong> ({deletingProvider.defaultModel})?
            </p>

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setDeletingProvider(null)}
                className="px-4 py-2 rounded-xl text-slate-600 hover:text-slate-900 text-xs font-medium cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirmed}
                disabled={saving}
                className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold text-xs shadow-sm cursor-pointer"
              >
                {saving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

